/**
 * Route guard that requires authentication.
 *
 * Optionally enforces that the user has changed their temporary password
 * (used for all main app routes, but NOT for /change-password itself).
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Spinner } from '@/components/ui'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** If true, redirects to /change-password when must_change_password is set. */
  requirePasswordChanged?: boolean
}

export function ProtectedRoute({ children, requirePasswordChanged = false }: ProtectedRouteProps) {
  const { user, accessToken, isInitialized, isLoading, mustChangePassword, impersonation } = useAuthStore()
  const location = useLocation()

  // Still loading initial session check
  if (!isInitialized || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="h-8 w-8" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Not authenticated — redirect to login
  if (!user || !accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Authenticated but must change password. Under a View-as (SA-906 D7) the
  // flag is the TARGET's obligation, which the admin must never satisfy for
  // them, so the redirect is suppressed.
  if (requirePasswordChanged && mustChangePassword && !impersonation) {
    return <Navigate to="/change-password" replace />
  }

  return <>{children}</>
}
